import { Link } from 'react-router-dom';

/**
 * Widgets visuais das super páginas de PONTO TURÍSTICO (/ponto-turistico/{handle}).
 * Deixa a página visual em vez de só texto: ficha com ícones, destaques em cards,
 * mini-mapa por localização e FAQ em acordeão. 100% client-side.
 */

/** Ficha rápida visual — cards com ícone para os dados operacionais. */
export function AtracaoFicha({ entity }: { entity: any }) {
  const e = entity;
  const gratis = typeof e.price === 'string' && /grát|gratuit/i.test(e.price);
  const items = [
    { icon: '🎟️', label: 'Entrada', value: gratis ? 'Gratuito' : e.price || '—' },
    e.duration && e.duration !== '-' ? { icon: '⏱️', label: 'Tempo de visita', value: e.duration } : null,
    e.difficulty ? { icon: '🥾', label: 'Acesso', value: e.difficulty } : null,
    e.bestTime && e.bestTime !== '-' ? { icon: '🕐', label: 'Melhor hora', value: e.bestTime } : null,
    e.vilarejoName ? { icon: '📍', label: 'Onde fica', value: e.vilarejoName } : null,
    typeof e.kidsFriendly === 'boolean' ? { icon: e.kidsFriendly ? '👨‍👩‍👧' : '🚫', label: 'Com crianças', value: e.kidsFriendly ? 'Tranquilo' : 'Exige atenção' } : null,
  ].filter(Boolean) as { icon: string; label: string; value: string }[];

  return (
    <div className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <h2 className="font-display text-lg text-warm-dark mb-4 tracking-wide">Ficha rápida</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map(it => (
          <div key={it.label} className="flex items-start gap-3 p-3 rounded-xl bg-sand-50 border border-sand-100">
            <span className="text-2xl flex-shrink-0" aria-hidden="true">{it.icon}</span>
            <div className="min-w-0">
              <p className="font-body text-[11px] uppercase tracking-wide text-warm-gray">{it.label}</p>
              <p className="font-body text-sm font-semibold text-warm-dark leading-snug">{it.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Destaques em cards visuais (em vez de lista de bullets). */
export function AtracaoDestaques({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h2 className="flex items-center gap-2 font-display text-xl text-warm-dark mb-4 tracking-wide">
        ✨ Destaques
      </h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((h, i) => (
          <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-teal-light/5 border border-teal-light/20">
            <span className="text-teal-deep font-bold flex-shrink-0 mt-0.5">◆</span>
            <p className="font-body text-sm text-warm-gray leading-relaxed">{h}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Dicas em cards. */
export function AtracaoDicas({ items }: { items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h2 className="flex items-center gap-2 font-display text-xl text-warm-dark mb-4 tracking-wide">
        💡 Dicas de quem conhece
      </h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((tip, i) => (
          <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-sand-200 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
            <span className="text-coral flex-shrink-0 mt-0.5">✓</span>
            <p className="font-body text-sm text-warm-gray leading-relaxed">{tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mini-mapa do Google centrado na coordenada do ponto. */
export function AtracaoMapa({ entity }: { entity: any }) {
  const geo = entity?.schemaOrg?.geo;
  if (!geo?.latitude || !geo?.longitude) return null;
  const src = `https://maps.google.com/maps?q=${geo.latitude},${geo.longitude}&z=15&output=embed`;
  return (
    <div id="mapa" className="bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">Onde fica na ilha</h2>
      <div className="rounded-xl overflow-hidden border border-sand-200">
        <iframe
          title={`Mapa — ${entity.title}`}
          src={src}
          className="w-full h-64 sm:h-80"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <p className="font-body text-xs text-warm-gray mt-2">
        Localização aproximada na Ilha do Mel. Não há endereços com rua — a referência é a trilha/vila mais próxima.
      </p>
    </div>
  );
}

/** FAQ em acordeão + JSON-LD FAQPage. */
export function AtracaoFAQ({ entity }: { entity: any }) {
  const faq = entity?.faq;
  if (!faq || faq.length === 0) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((f: any) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
  return (
    <div id="faq">
      <h2 className="font-display text-xl text-warm-dark mb-4 tracking-wide">Perguntas frequentes</h2>
      <div className="space-y-3">
        {faq.map((f: any, i: number) => (
          <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
            <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
              <span className="font-display text-base text-warm-dark pr-4">{f.q}</span>
              <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
            </summary>
            <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </div>
  );
}

/** CTA de navegação reutilizável para a pilar da categoria (praias, pousadas, etc.). */
export function NavegacaoPilar({ to, label }: { to: string; label: string }) {
  return (
    <div className="bg-teal-deep rounded-2xl p-6 text-center">
      <h3 className="font-display text-xl text-white mb-2">Quer explorar mais?</h3>
      <p className="font-body text-sand-100 text-sm mb-4">
        Veja o guia completo com {label} da Ilha do Mel.
      </p>
      <Link
        to={to}
        className="inline-flex items-center gap-2 font-body text-sm font-semibold bg-white text-teal-deep hover:bg-sand-100 rounded-lg px-5 py-2.5 transition-colors"
      >
        Ver {label} da Ilha do Mel →
      </Link>
    </div>
  );
}

/** Composição visual completa das super páginas de ponto turístico. */
export function AtracaoVisualSections({ entity }: { entity: any }) {
  return (
    <>
      <AtracaoFicha entity={entity} />
      <AtracaoDestaques items={entity.highlights} />
      <AtracaoDicas items={entity.tips} />
      <AtracaoFAQ entity={entity} />
    </>
  );
}
